'use client';

/**
 * Public tax-exemption request panel (body of /tax-exemption).
 *
 * Submits through the existing `/api/account/tax-exemption` endpoint, which is
 * what funnels the request into the pending queue AND fires the Make → ClickUp
 * → #admin-requests Slack notification (`createRequest` in
 * tax-exemption-request.service.ts). Nothing new is written here.
 *
 * Submitting requires being signed in. That is not new friction invented for
 * this page: an exemption is granted to a customer account (and cascaded across
 * a company email domain), so there is nothing to attach an anonymous upload
 * to — the admin-side form rejects an unknown email for the same reason. The
 * page itself is fully public; only the upload asks for sign-in.
 */

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { CheckCircle2, Clock, FileText, LogIn, ShieldCheck, UploadCloud, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

const PAGE_PATH = '/tax-exemption';

interface ExemptionStatus {
  isExempt: boolean;
  exemptRegions: string | null;
  hasPending: boolean;
}

interface ApiErrorResponse {
  error?: string;
}

function mapApiError(code: string | undefined): string {
  switch (code) {
    case 'request_already_pending':
      return 'You already have a request under review.';
    case 'document_required':
      return 'Please attach at least one document.';
    case 'too_many_files':
      return 'Too many files (max 10).';
    case 'file_too_large':
      return 'A file is too large (max 10MB).';
    case 'unsupported_file_type':
      return 'Unsupported file type. Please upload a PDF or an image.';
    case 'unauthorized':
      return 'Please sign in again to submit your documents.';
    case 'RATE_LIMITED':
      return 'Too many attempts. Please wait a minute and try again.';
    default:
      return 'Submission failed. Please try again.';
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="my-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      {children}
    </section>
  );
}

export function TaxExemptionRequestPanel() {
  const { status: sessionStatus } = useSession();
  const [status, setStatus] = useState<ExemptionStatus | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/account/tax-exemption');
      if (res.ok) {
        setStatus((await res.json()) as ExemptionStatus);
      } else {
        // 401 here means a signed-in session without a customer record (e.g. an
        // admin-only login). Fall through to the form and let the POST answer.
        setStatus(null);
      }
    } catch {
      setStatus(null);
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (sessionStatus !== 'authenticated') return;
    void loadStatus();
  }, [sessionStatus, loadStatus]);

  function addFiles(list: FileList | null) {
    if (!list) return;
    // Copy the FileList NOW, not inside the updater. `list` is the input's live
    // file list, and the caller clears the input (`value = ''`) on the very next
    // line so re-picking the same file still fires a change event — clearing
    // empties this same object. React can run a functional updater after the
    // handler returns, and here it did: the copy came back empty, no chip
    // rendered, and submit reported "Please attach at least one document."
    const picked = Array.from(list);
    setFiles((prev) => [...prev, ...picked]);
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (files.length === 0) {
      setError('Please attach at least one document.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('file', f));
      const res = await fetch('/api/account/tax-exemption', { method: 'POST', body: fd });
      if (!res.ok) {
        // The rate limiter puts a human message in `error` and its code in
        // `code`, so key off the status rather than the body for 429.
        if (res.status === 429) {
          setError(mapApiError('RATE_LIMITED'));
          return;
        }
        const body = (await res.json().catch(() => ({}))) as ApiErrorResponse;
        setError(mapApiError(body.error));
        return;
      }
      setFiles([]);
      setSubmitted(true);
      await loadStatus();
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── signed out ───────────────────────────────────────────────────────────
  if (sessionStatus === 'unauthenticated') {
    return (
      <Shell>
        <div className="flex items-start gap-4">
          <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--scw-navy)]/10 text-[var(--scw-navy)]">
            <LogIn className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-[var(--scw-navy)]">
              Sign in to submit your certificate
            </h3>
            <p className="mt-1 text-sm text-[var(--scw-gray-body)]">
              Exemption is applied to your account, and to everyone on your company email domain,
              so we need to know whose account to attach it to.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                asChild
                className="bg-[var(--scw-orange)] text-white hover:bg-[var(--scw-orange-hover)]"
              >
                <Link href={`/login?callbackUrl=${encodeURIComponent(PAGE_PATH)}`}>Sign in</Link>
              </Button>
              <Button asChild variant="outline">
                <Link href="/register">Create an account</Link>
              </Button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ── loading ──────────────────────────────────────────────────────────────
  if (sessionStatus === 'loading' || !statusLoaded) {
    return (
      <Shell>
        <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
      </Shell>
    );
  }

  // ── already under review ─────────────────────────────────────────────────
  if (submitted || status?.hasPending) {
    return (
      <Shell>
        <div className="flex items-start gap-4">
          <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
            <Clock className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              Under review
            </span>
            <p className="mt-1.5 text-sm text-[var(--scw-gray-body)]">
              Thanks. Your documents are with our team and we will email you once the review is
              done.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  // ── upload form (also the "update my certificate" path for exempt accounts)
  return (
    <Shell>
      {status?.isExempt && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-700" />
          <p className="text-sm text-green-800">
            Your account is already tax-exempt
            {status.exemptRegions ? ` for ${status.exemptRegions}` : ''}. Submit a new document
            below only if your certificate was renewed or your exempt states changed.
          </p>
        </div>
      )}

      <div className="flex items-start gap-4">
        <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--scw-navy)]/10 text-[var(--scw-navy)]">
          <ShieldCheck className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-[var(--scw-navy)]">
            Submit your tax exempt form
          </h3>
          <p className="mt-1 text-sm text-[var(--scw-gray-body)]">
            Attach your exemption certificate. Our team reviews it and assigns the exempt states.
          </p>

          <form onSubmit={submit} className="mt-4 space-y-4">
            <label
              htmlFor="tax-exemption-docs"
              className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-7 text-center transition-colors hover:border-[var(--scw-navy)]/40 hover:bg-gray-100"
            >
              <UploadCloud className="size-6 text-[var(--scw-gray-muted)]" />
              <span className="text-sm font-medium text-[var(--scw-navy)]">Click to upload</span>
              <span className="text-xs text-[var(--scw-gray-muted)]">
                PDF or image, up to 10 files (10MB each)
              </span>
              <input
                id="tax-exemption-docs"
                type="file"
                multiple
                accept="application/pdf,image/*"
                className="sr-only"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>

            {files.length > 0 && (
              <ul className="space-y-1.5">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm"
                  >
                    <FileText className="size-4 shrink-0 text-[var(--scw-gray-muted)]" />
                    <span className="min-w-0 flex-1 truncate text-[var(--scw-gray-body)]">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-[var(--scw-gray-muted)] transition-colors hover:text-red-600"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {error && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="bg-[var(--scw-orange)] text-white hover:bg-[var(--scw-orange-hover)]"
            >
              {submitting ? 'Submitting…' : 'Submit for review'}
            </Button>
          </form>
        </div>
      </div>
    </Shell>
  );
}
